import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import DestinationGlobe from './DestinationGlobe';
import './Uploader.css';

const MAX_BYTES = 50 * 1024 * 1024;
const RELATIONSHIPS = [
  'Self (the student)',
  'Parent',
  'Sibling',
  'Other relative',
  'Teacher',
  'Friend',
];

function safeFileName(name) {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);
}

function isMediaFile(file) {
  return /^image\//.test(file.type) || /^video\//.test(file.type);
}

export default function Uploader({ initialStudentId }) {
  const [studentIdInput, setStudentIdInput] = useState(initialStudentId || '');
  const [student, setStudent] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [relationship, setRelationship] = useState('');
  const [uploads, setUploads] = useState([]); // {id, file, progress, status, error, downloadURL, caption}
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (initialStudentId) {
      lookupStudent(initialStudentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStudentId]);

  async function lookupStudent(id) {
    if (!/^\d+$/.test(id.trim())) {
      setLookupError('Student ID is numbers only.');
      return;
    }
    setLookingUp(true);
    setLookupError(null);
    try {
      const snap = await getDoc(doc(db, 'students', id.trim()));
      if (snap.exists()) {
        setStudent({ id: id.trim(), ...snap.data() });
      } else {
        setStudent(null);
        setLookupError("We couldn't find that student ID. Double-check the number and try again.");
      }
    } catch (err) {
      console.error(err);
      setLookupError('Lookup hiccup. Try again in a moment.');
    } finally {
      setLookingUp(false);
    }
  }

  function onIdSubmit(e) {
    e.preventDefault();
    lookupStudent(studentIdInput);
  }

  function addFiles(fileList) {
    const next = [];
    for (const file of fileList) {
      if (!isMediaFile(file)) {
        next.push({ id: cryptoRandom(), file, status: 'rejected', error: 'Only images or videos.' });
        continue;
      }
      if (file.size > MAX_BYTES) {
        next.push({ id: cryptoRandom(), file, status: 'rejected', error: 'Larger than 50MB.' });
        continue;
      }
      next.push({ id: cryptoRandom(), file, status: 'pending', progress: 0, caption: '' });
    }
    setUploads((prev) => [...prev, ...next]);
    next.forEach((u) => { if (u.status === 'pending') startUpload(u); });
  }

  function startUpload(item) {
    if (!student) return;
    const path = `media-uploads/${student.id}/${Date.now()}_${safeFileName(item.file.name)}`;
    const ref = storageRef(storage, path);
    const task = uploadBytesResumable(ref, item.file, { contentType: item.file.type });

    setUploadField(item.id, { status: 'uploading' });

    task.on(
      'state_changed',
      (snap) => {
        const pct = (snap.bytesTransferred / snap.totalBytes) * 100;
        setUploadField(item.id, { progress: pct });
      },
      (err) => {
        console.error('upload failed', err);
        setUploadField(item.id, { status: 'failed', error: err.message || 'Upload failed.' });
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          await addDoc(collection(db, 'mediaUploads'), {
            studentId: student.id,
            studentName: student.name || null,
            program: student.program || null,
            uploaderRelationship: relationship || null,
            fileName: item.file.name,
            storagePath: path,
            contentType: item.file.type,
            sizeBytes: item.file.size,
            downloadURL: url,
            caption: '',
            createdAt: serverTimestamp(),
          });
          setUploadField(item.id, { status: 'done', downloadURL: url, progress: 100 });
        } catch (err) {
          console.error('mirror failed', err);
          setUploadField(item.id, { status: 'failed', error: 'Saved file, but record failed.' });
        }
      }
    );
  }

  function setUploadField(id, patch) {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }

  function onDrop(e) {
    e.preventDefault();
    setDragActive(false);
    if (!student || !relationship) return;
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  }

  function onPick(e) {
    if (!student || !relationship) return;
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = '';
  }

  const allDone = uploads.length > 0 && uploads.every((u) => u.status === 'done' || u.status === 'failed' || u.status === 'rejected');
  const completedCount = uploads.filter((u) => u.status === 'done').length;
  const ready = student && relationship;

  return (
    <div className="uploader">
      <section className="uploader__hero glass-card">
        <div className="uploader__hero-text">
          <p className="eyebrow">Memories from the journey</p>
          <h1>Send a postcard from your CTE adventure.</h1>
          <p>
            Drop in photos and short videos from your program of study. Projects, competitions,
            field trips, lab days. They'll be cued up in the slideshow at the ceremony.
          </p>
        </div>
        <div className="uploader__hero-globe" aria-hidden="true">
          <DestinationGlobe size={220} />
        </div>
      </section>

      {!student ? (
        <form className="glass-card uploader__lookup" onSubmit={onIdSubmit}>
          <p className="eyebrow">Step 1 · Find the senior</p>
          <label htmlFor="uploader-sid" className="uploader__label">
            Student ID of the senior whose memories you're uploading
          </label>
          <div className="uploader__row">
            <input
              id="uploader-sid"
              type="text"
              inputMode="numeric"
              pattern="\d*"
              className="field-input"
              placeholder="e.g. 123456"
              value={studentIdInput}
              onChange={(e) => setStudentIdInput(e.target.value)}
              required
            />
            <button className="btn-primary" type="submit" disabled={lookingUp}>
              {lookingUp ? <span className="spinner" /> : 'Look up'}
            </button>
          </div>
          {lookupError && <p className="uploader__error">{lookupError}</p>}
        </form>
      ) : (
        <section className="glass-card uploader__student">
          <p className="eyebrow">Reservation found</p>
          <div className="uploader__student-row">
            <div>
              <h2 className="uploader__student-name">{student.name}</h2>
              {student.program && <span className="destination-tag">{student.program}</span>}
            </div>
            <button
              className="btn-ghost"
              type="button"
              onClick={() => { setStudent(null); setUploads([]); setRelationship(''); setStudentIdInput(''); }}
            >
              Change
            </button>
          </div>
        </section>
      )}

      {student && (
        <section className="glass-card">
          <p className="eyebrow">Step 2 · Who's sending these?</p>
          <div className="uploader__rel-grid">
            {RELATIONSHIPS.map((r) => (
              <button
                key={r}
                type="button"
                className={`uploader__rel ${relationship === r ? 'is-active' : ''}`}
                onClick={() => setRelationship(r)}
              >
                {r}
              </button>
            ))}
          </div>
        </section>
      )}

      {ready && (
        <section
          className={`glass-card uploader__drop ${dragActive ? 'is-active' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
        >
          <p className="eyebrow">Step 3 · Drop your memories</p>
          <h3 className="uploader__drop-title">Drag photos &amp; videos here</h3>
          <p className="uploader__drop-sub">or tap to choose from your phone. Up to 50MB each, images and videos only.</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={onPick}
            className="uploader__file-input"
          />
          <button type="button" className="btn-primary" onClick={() => fileInputRef.current?.click()}>
            Choose files
          </button>
        </section>
      )}

      {uploads.length > 0 && (
        <section className="glass-card uploader__list">
          <p className="eyebrow">{allDone ? 'Postcards delivered' : 'In transit'}</p>
          <ul>
            {uploads.map((u) => (
              <li key={u.id} className={`uploader__item is-${u.status}`}>
                <div className="uploader__item-head">
                  <span className="uploader__item-name" title={u.file.name}>{u.file.name}</span>
                  <span className="uploader__item-status">
                    {u.status === 'rejected' && 'Skipped'}
                    {u.status === 'pending'  && 'Queued'}
                    {u.status === 'uploading' && `${Math.round(u.progress || 0)}%`}
                    {u.status === 'done'     && 'Delivered'}
                    {u.status === 'failed'   && 'Failed'}
                  </span>
                </div>
                {(u.status === 'uploading' || u.status === 'done') && (
                  <div className="uploader__bar">
                    <div className="uploader__bar-fill" style={{ width: `${u.progress || 0}%` }} />
                  </div>
                )}
                {u.error && <p className="uploader__error">{u.error}</p>}
              </li>
            ))}
          </ul>
          {allDone && completedCount > 0 && (
            <p className="uploader__success">
              {completedCount} {completedCount === 1 ? 'memory' : 'memories'} added to the slideshow. You can keep dropping more above.
            </p>
          )}
        </section>
      )}
    </div>
  );
}

function cryptoRandom() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
